import Markdoc, { type Node, type RenderableTreeNode } from '@markdoc/markdoc';
import { grammar } from 'utils/shared/editorialMarkdoc';

export const maxRenderNodes = 50_000;
export const maxRenderedBytes = 1024 * 1024;

/** Budget standard HTML and conservative approved-component output together. */
export function assertRenderedBudget(asts: Node[]): number {
  let nodes = 0;
  let renderedBytes = 0;
  function addNodes(count: number) {
    nodes += count;
    if (nodes > maxRenderNodes)
      throw new Error('Editorial body/FAQ exceeds the 50000-node rendering budget');
  }
  // React escapes apostrophes; Markdoc's HTML renderer does not.
  function apostropheBytes(value: RenderableTreeNode): number {
    if (typeof value === 'string') return (value.match(/'/g)?.length || 0) * 5;
    if (!Markdoc.Tag.isTag(value)) return 0;
    return (
      Object.values(value.attributes).reduce<number>(
        (sum, entry) =>
          sum + (typeof entry === 'string' ? (entry.match(/'/g)?.length || 0) * 5 : 0),
        0,
      ) + value.children.reduce<number>((sum, child) => sum + apostropheBytes(child), 0)
    );
  }
  function project(value: RenderableTreeNode): RenderableTreeNode {
    if (!Markdoc.Tag.isTag(value)) return value;
    if (value.name === 'ContentImage') {
      renderedBytes += 8 * 1024;
      const { src, alt, width, height } = value.attributes;
      return new Markdoc.Tag('figure', {}, [new Markdoc.Tag('img', { src, alt, width, height })]);
    }
    if (value.name === 'NewsletterCta') {
      renderedBytes += 16 * 1024;
      return '';
    }
    if (value.name === 'ContentTable') {
      const rows: string[][] = JSON.parse(value.attributes.rows);
      addNodes(5 + rows.length + rows.reduce((sum, row) => sum + row.length, 0));
      const [head, ...body] = rows;
      return new Markdoc.Tag('div', { className: 'table-scroll' }, [
        new Markdoc.Tag('table', {}, [
          new Markdoc.Tag('thead', {}, [
            new Markdoc.Tag(
              'tr',
              {},
              head.map((cell) => new Markdoc.Tag('th', { scope: 'col' }, [cell])),
            ),
          ]),
          new Markdoc.Tag(
            'tbody',
            {},
            body.map(
              (row) =>
                new Markdoc.Tag(
                  'tr',
                  {},
                  row.map((cell) => new Markdoc.Tag('td', {}, [cell])),
                ),
            ),
          ),
        ]),
      ]);
    }
    return new Markdoc.Tag(value.name, value.attributes, value.children.map(project));
  }
  for (const ast of asts) {
    for (const _node of ast.walk()) {
      addNodes(1);
    }
    const tree = project(Markdoc.transform(ast, grammar));
    const html = Markdoc.renderers.html(tree);
    renderedBytes += new TextEncoder().encode(html).byteLength + apostropheBytes(tree);
    if (renderedBytes > maxRenderedBytes)
      throw new Error('Editorial body/FAQ exceeds the 1MiB rendered response budget');
  }
  return renderedBytes;
}
